import { bootstrapEndpoint } from "@springtree/eva-sdk-core-service";
import { hooks } from "@springtree/eva-sdk-react-recoil";
import { MagicLinkHub } from "@springtree/eva-sdk-signalr";
import { Core } from "@springtree/eva-services-core";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { useAuthContext } from "~/contexts/auth";
import Logger from "~/services/logger/logger";
import { SignalRAction } from "~/shared/signalr-action.enum";

export function useMagicLinkSignalr({
  action,
  onLoginResponse,
}: {
  action: SignalRAction;
  onLoginResponse: (response: EVA.Core.LoginResponse) => Promise<void>;
}) {
  const [hub, setHub] = useState<MagicLinkHub | null>(null);
  const router = useRouter();
  const { logout } = useAuthContext();

  const login = hooks.useCallService({ service: Core.Login });
  const sendMagicLinkService = hooks.useCallService({ service: Core.SendMagicLink });

  async function sendMagicLink(email: string, orderId: number) {
    await logout();
    let errorResponse: Error;
    // 0. Send the magic link via email
    const response = await sendMagicLinkService(
      {
        EmailAddress: email,
        RedirectUrl: `${window.location.origin}/${router.locale}/email-verified/?action=${action}&orderId=${orderId}`,
        OrderID: orderId,
      },
      undefined,
      {
        onError: (error) => {
          Logger.instance.error(error);
          errorResponse = error;
          return errorResponse;
        },
      }
    );

    if (!response) {
      throw new Error(`Unexpected empty response from Core.SendMagicLink`);
    }

    // 1. Set the event handler
    hub?.setEventHandler("Confirmed", async (payload) => {
      const loginResponse = await login({
        Username: email,
        // @ts-expect-error - ApiKey instead of TemporaryToken
        AuthenticationToken: payload.ApiKey,
        Password: payload.TemporaryToken,
      });

      onLoginResponse(loginResponse!);
      hub.leave();
    });

    // 2. Actually join the hub and wait for the event
    await hub?.join({ subscriberName: "kiko-uc/useMagicLinkSignalr" });
    await hub?.subscribeToMagicLink({ RequestToken: response.RequestToken });
  }

  useEffect(() => {
    bootstrapEndpoint({
      uri: process.env.NEXT_PUBLIC_EVA_BACKEND_URL!,
    }).then((endpoint) => {
      setHub(new MagicLinkHub(endpoint));
    });
  }, []);

  return {
    hub,
    sendMagicLink,
  };
}
